import React, { FormEventHandler, useState } from "react";
import { useTheme } from "styled-components";
import { Container } from "../../Container/Container";
import { Accordion, AccordionProps } from "../../Accordion/Accordion";
import {
  FaqsContainer,
  FaqText,
  FaqsWrapper,
  StyledInput,
  SearchForm,
  FormError,
} from "./SliceFaqs.styles";
import { Slugify } from "../../../../helpers/slugify";
import { useDebounceCallback } from "../../../../hooks/useDebounceCallback";

export enum FAQSliceType {
  default = "default",
  contained = "contained",
}

export interface PureSliceFaqsProps {
  content: JSX.Element | React.ReactNode | string;
  faqs?: AccordionProps[];
  type?: keyof typeof FAQSliceType;
}

export function PureSliceFaqs({
  content,
  faqs,
  type = "default",
  ...props
}: PureSliceFaqsProps) {
  const theme = useTheme();
  const isDark = theme.theme.page === "dark";
  const [faqsList, setFaqsList] = useState<AccordionProps[] | undefined>(faqs);
  const [formError, setFormError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");

  const doSearch = (search: string) => {
    if (!search) {
      setFormError(null);
      return;
    }

    if (search.length < 3) {
      setFormError("Type in more than 3 characters to search");
      setFaqsList(faqs);
      return;
    }

    setFormError(null);

    const filteredFaqs = faqs?.filter((faq) => {
      return faq.question.includes(search);
    });

    if (filteredFaqs?.length && filteredFaqs.length > 0) {
      setFaqsList(filteredFaqs);
    } else {
      setFormError(`No results found for ${search}`);
      setFaqsList(faqs);
    }
  };

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    doSearch(searchValue);
  };

  const debouncedSearch = useDebounceCallback(doSearch, 300);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleInputBlur = () => {
    debouncedSearch.cancel?.(); // Cancel any pending debounce
    doSearch(searchValue);
  };

  return (
    <Container className={isDark ? "dark-background" : ""} {...props}>
      <FaqsContainer variant={type}>
        {content && <FaqText variant={type}>{content}</FaqText>}

        {faqs?.length && faqs.length > 10 && (
          <SearchForm onSubmit={handleSearch}>
            <StyledInput
              type="text"
              name="search"
              placeholder="Search the FAQs..."
              min={3}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              autoComplete="off"
            />
            {formError && <FormError>{formError}</FormError>}
          </SearchForm>
        )}

        <FaqsWrapper>
          {faqsList?.map((faq, i: number) => {
            return (
              <Accordion
                key={`${Slugify(faq?.question || "")}-${i}`}
                question={faq?.question}
              >
                {faq.children}
              </Accordion>
            );
          })}
        </FaqsWrapper>
      </FaqsContainer>
    </Container>
  );
}
